import type { DisplayTemplateAst } from '#layers/data-model/types/display-template';
import type { GlobalFieldId } from '#layers/data-model/types/schema/ids';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { DisplayTemplateAstType } from '#layers/data-model/types/display-template';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { isSingleRelationField } from '#layers/data-model/utils/schema/collection';

/**
 * Resolve a display template AST to a plain text string by extracting field values from an item.
 *
 * Useful where only a string is accepted (e.g. Schedule-X calendar event titles).
 * For rich rendering, use the `RenderDisplayTemplate` component instead.
 */
export function resolveDisplayTemplatePlainText(
	ast: DisplayTemplateAst | null | undefined,
	item: AnyItem,
	collection: ResolvedMonospaceCollection,
): string | null {
	if (!ast?.content?.length) return null;

	const parts: string[] = [];

	for (const node of ast.content) {
		if (node.type === DisplayTemplateAstType.Text) {
			parts.push(node.text);
		}
		else if (node.type === DisplayTemplateAstType.Ref) {
			const fieldIds = Array.isArray(node.attrs.fieldId) ? node.attrs.fieldId : [node.attrs.fieldId];
			const value = resolveFieldPath(fieldIds, item, collection);

			if (value != null) {
				parts.push(String(value));
			}
		}
	}

	const result = parts.join('').trim();
	return result.length > 0 ? result : null;
}

function resolveFieldPath(
	fieldIds: GlobalFieldId[],
	item: AnyItem,
	collection: ResolvedMonospaceCollection,
): unknown {
	if (fieldIds.length === 0 || !item) return null;

	const [firstId, ...rest] = fieldIds;
	const field = findCollectionFieldById(collection, firstId!);

	if (!field) return null;

	const value = item[field.apiName];

	if (rest.length === 0) return value;

	if (value == null || typeof value !== 'object') return null;

	if (isSingleRelationField(field) && field.oppositeCollection) {
		return resolveFieldPath(rest, value as AnyItem, field.oppositeCollection as ResolvedMonospaceCollection);
	}

	return null;
}
